import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TablerIconsModule } from 'angular-tabler-icons';
import { debounceTime, distinctUntilChanged, finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { PaginationResult } from 'src/app/models/common.response';
import { MatPaginator } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DoctorSpecialityService } from './doctor-speciality.service';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { DoctorSpecialityDetailComponent } from './doctor-speciality-detail/doctor-speciality-detail.component';
import { DeleteDialog } from 'src/app/pages/dialogs/delete.component';
import { PaginationRequest } from 'src/app/models/common.request';
import { DoctorSpeciality } from './idoctor-speciality';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';


@Component({
  selector: 'app-doctor-speciality',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule
  ],
  templateUrl: './doctor-speciality.component.html',
  styleUrl: './doctor-speciality.component.scss'
})
export class DoctorSpecialityComponent implements OnInit, OnDestroy, AfterViewInit {
  displayedColumns: string[] = [
    'name',
    'Default',
    'action'
  ];
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  pagination: PaginationResult;
  dataSource = new MatTableDataSource<DoctorSpeciality>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);
  isLoading: boolean = false;
  searchSpeciality: FormControl = new FormControl();
  specialitycount: number = 0;
  specialitydata: DoctorSpeciality[];
  pageSizeOptions = PAGE_SIZE_OPTIONS;


  constructor(
    private _specialityService: DoctorSpecialityService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils
  ) { }

  ngOnInit(): void {
    this._specialityService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.specialitycount = this.pagination.rowCount;
        }
      });

    this._specialityService.specialities$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.specialitydata = data;
        this.dataSource = new MatTableDataSource(this.specialitydata); // Correct initialization
        this.dataSource.paginator = this.paginator;
      });

    this.searchSpeciality.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        return this._specialityService.getAllSpeciality({
          pageNumber: 0,
          pageSize: this.pagination.pageSize,
          isDescending: false,
          searchValue: query || ''
        }).pipe(
          finalize(() => (this.isLoading = false))
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.specialities;
        this.specialitycount = response.result.pagination.rowCount;
      }
    });

  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  onAddSpeciality() {
    this.dialog.open(DoctorSpecialityDetailComponent, {
      width: '600px',
      height: 'auto',
      disableClose: true,
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.loadSpeciality();
      }
    });
  }

  onUpdateSpeciality(speciality: DoctorSpeciality) {
    this.dialog.open(DoctorSpecialityDetailComponent, {
      width: '600px',
      height: 'auto',
      disableClose: true,
      data: {
        Doctorspeciality: speciality,
        isEdit: true,
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.loadSpeciality();
      }
    });
  }

  onDeleteSpeciality(id: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.SPECIALITY') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        this._specialityService.deleteSpeciality(id).subscribe((response) => {
          if (response.isSuccess) {
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.SPECIALITY')));
            this.loadSpeciality();
          }
          else {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join("\n"));

          }
        })
      }
    });
  }

  loadSpeciality() {
    const globalSearchValue = this.searchSpeciality?.value?.trim();
    const requestPayload: PaginationRequest = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false
    };

    if (globalSearchValue) {
      requestPayload.searchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    this._specialityService.getAllSpeciality(requestPayload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response) => {
        if (response && response.result) {
          const specialities = response.result.specialities;
          this.specialitydata = specialities;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(specialities);
          } else {
            this.dataSource.data = specialities;
          }
          this.dataSource.paginator = this.paginator;
          this.specialitycount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });
  }

}
